import type { RoundSessionResponse } from '../services/api'

type Payload = Record<string, unknown>
type Point = { latitude: number; longitude: number; capturedAt?: string; accuracyMetres?: number }

export interface ClubDistanceStat { club: string; samples: number; averageMetres: number; medianMetres: number }
export interface RoundHistoryStats {
  rounds: number
  completedRounds: number
  holesWithScores: number
  averageHoleScore?: number
  clubDistances: ClubDistanceStat[]
  fairways: { known: number; hit: number; left: number; right: number; other: number }
  gir: { known: number; hit: number }
  putts: { knownHoles: number; total: number; average?: number }
  chips: { knownHoles: number; total: number; average?: number }
}

const parse = (value: string): Payload => {
  try { return JSON.parse(value) as Payload } catch { return {} }
}
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const point = (value: unknown): Point | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as Payload
  const latitude = Number(candidate.latitude), longitude = Number(candidate.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return undefined
  const accuracyMetres = candidate.accuracyMetres === undefined ? undefined : Number(candidate.accuracyMetres)
  if (accuracyMetres !== undefined && (!Number.isFinite(accuracyMetres) || accuracyMetres < 0 || accuracyMetres > 100)) return undefined
  return { latitude, longitude, capturedAt: typeof candidate.capturedAt === 'string' ? candidate.capturedAt : undefined, accuracyMetres }
}
const freshForEvent = (position: Point, occurredAt: string) => {
  if (!position.capturedAt) return true
  const captured = Date.parse(position.capturedAt), occurred = Date.parse(occurredAt)
  return Number.isFinite(captured) && Number.isFinite(occurred) && Math.abs(occurred - captured) <= 30_000
}
const metres = (a: Point, b: Point) => {
  const rad = Math.PI / 180, dLat = (b.latitude - a.latitude) * rad, dLon = (b.longitude - a.longitude) * rad
  const lat1 = a.latitude * rad, lat2 = b.latitude * rad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}
const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b), middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

export function calculateRoundHistoryStats(sessions: RoundSessionResponse[]): RoundHistoryStats {
  const distances = new Map<string, number[]>(), scores: number[] = []
  // New API responses carry an authoritative session flag. For pre-migration
  // responses, any immutable synthetic event classifies the whole legacy session
  // as demo so a historically mixed stream cannot leak into real statistics.
  const isDemo = (session: RoundSessionResponse) => session.isDemo === true ||
    (session.isDemo === undefined && session.events.some(e => parse(e.payloadJson).synthetic === true))
  const realSessions = sessions.filter(session => !isDemo(session))
  const result: RoundHistoryStats = {
    rounds: realSessions.length, completedRounds: realSessions.filter(s => s.status === 'completed').length, holesWithScores: 0,
    clubDistances: [], fairways: { known: 0, hit: 0, left: 0, right: 0, other: 0 }, gir: { known: 0, hit: 0 },
    putts: { knownHoles: 0, total: 0 }, chips: { knownHoles: 0, total: 0 },
  }
  for (const session of realSessions) {
    const ordered = [...session.events].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.eventId.localeCompare(b.eventId))
    const marks = new Map<string, { id: string; hole: number; club: string; position: Point; occurredAt: string; penalty: boolean; synthetic: boolean }>()
    const endpoints: { hole: number; position: Point; occurredAt: string }[] = []
    const holes = new Map<number, Payload>()
    for (const event of ordered) {
      const payload = parse(event.payloadJson), hole = Number(payload.holeNumber)
      if (event.type === 'shot-mark') {
        const p = point(payload.position)
        if (p && freshForEvent(p, event.occurredAt)) marks.set(event.eventId, { id: event.eventId, hole, club: String(payload.club ?? ''), position: p, occurredAt: event.occurredAt, penalty: payload.penalty === true, synthetic: payload.synthetic === true })
      } else if (event.type === 'shot-undone') marks.delete(String(payload.targetEventId ?? ''))
      else if (event.type === 'shot-corrected') {
        const current = marks.get(String(payload.targetEventId ?? ''))
        if (current) {
          const correctedPosition = payload.position === undefined ? current.position : point(payload.position)
          if (correctedPosition && freshForEvent(correctedPosition, event.occurredAt)) marks.set(current.id, { ...current, club: typeof payload.club === 'string' ? payload.club : current.club, position: correctedPosition, penalty: typeof payload.penalty === 'boolean' ? payload.penalty : current.penalty })
        }
      } else if (event.type === 'hole-result-set' || event.type === 'hole-finished') {
        holes.set(hole, payload)
        if (event.type === 'hole-finished') {
          const p = point(payload.position)
          if (p && freshForEvent(p, event.occurredAt)) endpoints.push({ hole, position: p, occurredAt: event.occurredAt })
        }
      }
    }
    const byHole = new Map<number, { position: Point; club: string; occurredAt: string; penalty: boolean; synthetic: boolean }[]>()
    for (const mark of marks.values()) byHole.set(mark.hole, [...(byHole.get(mark.hole) ?? []), mark])
    for (const endpoint of endpoints) byHole.set(endpoint.hole, [...(byHole.get(endpoint.hole) ?? []), { ...endpoint, club: 'Endpoint', penalty: false, synthetic: false }])
    for (const positions of byHole.values()) {
      positions.sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
      for (let i = 0; i < positions.length - 1; i++) {
        const start = positions[i], distance = metres(start.position, positions[i + 1].position)
        if (!start.penalty && !start.synthetic && start.club && start.club.toLowerCase() !== 'putter' && start.club !== 'Endpoint' && finite(distance) && distance > 0 && distance < 600) {
          distances.set(start.club, [...(distances.get(start.club) ?? []), distance])
        }
      }
    }
    for (const payload of holes.values()) {
      const score = Number(payload.score)
      if (Number.isFinite(score)) { scores.push(score); result.holesWithScores++ }
      const fairway = typeof payload.fairway === 'string' ? payload.fairway.toLowerCase() : undefined
      if (fairway && ['hit', 'left', 'right', 'other'].includes(fairway)) { result.fairways.known++; result.fairways[fairway as 'hit'|'left'|'right'|'other']++ }
      if (typeof payload.greenInRegulation === 'boolean') { result.gir.known++; if (payload.greenInRegulation) result.gir.hit++ }
      if (finite(payload.putts)) { result.putts.knownHoles++; result.putts.total += payload.putts }
      if (finite(payload.chips)) { result.chips.knownHoles++; result.chips.total += payload.chips }
    }
  }
  result.averageHoleScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : undefined
  result.putts.average = result.putts.knownHoles ? result.putts.total / result.putts.knownHoles : undefined
  result.chips.average = result.chips.knownHoles ? result.chips.total / result.chips.knownHoles : undefined
  result.clubDistances = [...distances.entries()].map(([club, values]) => ({ club, samples: values.length, averageMetres: values.reduce((a, b) => a + b, 0) / values.length, medianMetres: median(values) })).sort((a, b) => b.samples - a.samples || a.club.localeCompare(b.club))
  return result
}
